import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle2, XCircle, Clock, AlertTriangle, FileText,
  User, Calendar, ArrowRight, Loader2, RefreshCw, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAdminStore, useAuthStore, useUIStore } from '../lib/store';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';

const ApprovalsPage = () => {
  const { user } = useAuthStore();
  const { approvals, fetchApprovals, approveRequest, rejectRequest } = useAdminStore();
  const { language } = useUIStore();
  const { t } = useTranslation(language);
  
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    loadApprovals();
  }, [filter]);

  const loadApprovals = async () => {
    setIsLoading(true);
    try {
      await fetchApprovals(filter === 'all' ? null : filter);
    } catch (error) {
      toast.error(error.message || 'Failed to load approvals');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (approvalId) => {
    setIsProcessing(true);
    try {
      await approveRequest(approvalId);
      toast.success('Request approved');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectDialog) return;
    setIsProcessing(true);
    try {
      await rejectRequest(rejectDialog.id, rejectReason);
      toast.success('Request rejected');
      setRejectDialog(null);
      setRejectReason('');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getChangeDescription = (approval) => {
    const changes = approval.changes || {};
    const descriptions = [];
    
    if (changes.title) descriptions.push(`Title → "${changes.title}"`);
    if (changes.status) descriptions.push(`Status → ${changes.status}`);
    if (changes.priority) descriptions.push(`Priority → ${changes.priority}`);
    if (changes.assignee) descriptions.push(`Assignee changed`);
    if (changes.due_date) descriptions.push(`Due date → ${new Date(changes.due_date).toLocaleDateString()}`);
    if (changes.is_business_critical !== undefined) {
      descriptions.push(changes.is_business_critical ? 'Marked as Business Critical' : 'Unmarked as Business Critical');
    }
    
    return descriptions.length > 0 ? descriptions.join(', ') : 'Details changed';
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Clock className="h-6 w-6 text-primary" />
            {language === 'hi' ? 'अनुमोदन' : 'Approvals'}
          </h1>
          <p className="text-muted-foreground">
            {language === 'hi' ? 'परिवर्तन अनुरोधों की समीक्षा करें' : 'Review change requests from team members'}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={loadApprovals} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Approvals List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : approvals.length === 0 ? (
        <Card className="glass">
          <CardContent className="p-12 text-center">
            <CheckCircle2 className="h-16 w-16 text-chart-3 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold mb-2">
              {language === 'hi' ? 'कोई लंबित अनुमोदन नहीं' : 'No Pending Approvals'}
            </h3>
            <p className="text-muted-foreground">
              {language === 'hi' 
                ? 'सभी परिवर्तन अनुरोध संसाधित किए गए हैं।'
                : 'All change requests have been processed.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <AnimatePresence>
            {approvals.map((approval, index) => (
              <motion.div
                key={approval.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className={`glass overflow-hidden ${
                  approval.status === 'pending' ? 'border-l-4 border-l-chart-4' :
                  approval.status === 'approved' ? 'border-l-4 border-l-chart-3' :
                  'border-l-4 border-l-destructive'
                }`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {/* Header */}
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                            approval.entity_type === 'task' ? 'bg-primary/20' : 'bg-chart-1/20'
                          }`}>
                            <FileText className={`h-4 w-4 ${
                              approval.entity_type === 'task' ? 'text-primary' : 'text-chart-1'
                            }`} />
                          </div>
                          <div>
                            <span className="font-medium capitalize">
                              {approval.entity_type} {approval.change_type}
                            </span>
                            <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                              approval.status === 'pending' ? 'bg-chart-4/20 text-chart-4' :
                              approval.status === 'approved' ? 'bg-chart-3/20 text-chart-3' :
                              'bg-destructive/20 text-destructive'
                            }`}>
                              {approval.status}
                            </span>
                          </div>
                        </div>

                        {/* Changes */}
                        <div className="bg-white/5 rounded-lg p-3 mb-3">
                          <p className="text-sm text-muted-foreground mb-1">Changes:</p>
                          <p className="text-sm">{getChangeDescription(approval)}</p>
                          
                          {approval.original_values && Object.keys(approval.original_values).length > 0 && (
                            <div className="mt-2 pt-2 border-t border-white/10">
                              <p className="text-xs text-muted-foreground">
                                Original: {JSON.stringify(approval.original_values)}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Meta */}
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            Requested by: {approval.requested_by?.substring(0, 8)}...
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(approval.created_at)}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      {approval.status === 'pending' && (
                        <div className="flex items-center gap-2 ml-4">
                          <Button
                            size="sm"
                            onClick={() => handleApprove(approval.id)}
                            disabled={isProcessing}
                            className="gap-1"
                          >
                            <CheckCircle2 className="h-4 w-4" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setRejectDialog(approval)}
                            disabled={isProcessing}
                            className="gap-1 border-destructive/50 text-destructive hover:bg-destructive/10"
                          >
                            <XCircle className="h-4 w-4" />
                            Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Reject Dialog */}
      <Dialog open={!!rejectDialog} onOpenChange={() => setRejectDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-destructive" />
              Reject Request
            </DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting this change request.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Enter rejection reason (optional)..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReject}
              disabled={isProcessing}
            >
              {isProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Reject Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApprovalsPage;
